import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { usePopupState, bindMenu, bindTrigger } from "material-ui-popup-state/hooks";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TuneIcon from "@mui/icons-material/Tune";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import CreateKitVariantModal from "./CreateKitVariantModal";

import CustomKit from "./CustomKit";
import { KitAssetsProvider } from "../../../contexts/kit-assets";
import useRoles from "../../../hooks/user/useRoles";
import useMutation from "../../../hooks/useMutation";
import { CREATE_KIT_VARIANT_MUTATION } from "../../../constants/graphql/mutations";
import useNotification from "../../../hooks/notification";

const styles = {
  singleButton: {
    minWidth: 100,
    borderRadius: "25px",
    height: "36px",
  },
  leftButton: {
    marginRight: "1px",
    height: "36px",
    borderTopLeftRadius: "25px",
    borderBottomLeftRadius: "25px",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  rightButton: {
    padding: 0,
    height: "36px",
    minWidth: 36,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: "25px",
    borderBottomRightRadius: "25px",
  },
};

export default function AddToCartAction({ asset, notAvailable, addInventoryItem, defaultValues }) {
  const [open, setOpen] = useState();
  const [loading, setLoading] = useState(false);
  const addInFlightRef = useRef(false);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const [createVariant, { loading: creatingVariant }] = useMutation(CREATE_KIT_VARIANT_MUTATION);
  const { notifyError } = useNotification();
  const [variantAsset, setVariantAsset] = useState(null);
  const [variantOpen, setVariantOpen] = useState(false);
  const popupState = usePopupState({ variant: "popover", popupId: "add-to-cart-menu" });

  const handleToggle = () => {
    setOpen(v => !v);
  };

  const handleAddToCart = async () => {
    if (addInFlightRef.current) return;

    addInFlightRef.current = true;
    setLoading(true);
    try {
      await addInventoryItem();
    } finally {
      addInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleCreateVariant = async () => {
    try {
      const { data } = await createVariant({
        variables: { kitId: asset.id },
      });
      console.log("createKitVariant", data);
      const kitVariant = data.createKitVariant?.kitVariant;
      if (!kitVariant) throw new Error("Failed to create kit variant");
      setVariantAsset(kitVariant);
      setVariantOpen(true);
    } catch (e) {
      notifyError(e?.message || "Failed to create kit variant");
    } finally {
      popupState.close();
    }
  };

  if (asset.type !== "Kit") {
    return (
      <Button
        size="small"
        variant="outlined"
        disabled={notAvailable || loading}
        onClick={handleAddToCart}
        style={styles.singleButton}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          Add to Cart
        </Box>
      </Button>
    );
  }

  return (
    <Box display="flex" alignItems="center">
      <Button
        size="small"
        variant="outlined"
        disabled={notAvailable || loading}
        onClick={handleAddToCart}
        style={styles.leftButton}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {loading && <CircularProgress size={16} />}
          Add to Cart
        </Box>
      </Button>
      <Button
        size="small"
        variant="outlined"
        disabled={loading}
        {...bindTrigger(popupState)}
        style={styles.rightButton}
      >
        <KeyboardArrowDownIcon sx={{ transform: popupState.open ? undefined : "rotate(180deg)" }} />
      </Button>
      <Menu {...bindMenu(popupState)}>
        <MenuItem
          onClick={() => {
            handleToggle();
            popupState.close();
          }}
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Custom Kit" />
        </MenuItem>
        {asset?.type === "Kit" && isAdminOrManager && asset?.availableQuantity === 0 && (
          <MenuItem disabled={creatingVariant} onClick={handleCreateVariant}>
            <ListItemIcon>
              {creatingVariant ? (
                <CircularProgress size={16} />
              ) : (
                <ContentCopyIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText primary="Create Variant" />
          </MenuItem>
        )}
      </Menu>
      {open && (
        <KitAssetsProvider id={asset.id}>
          <CustomKit asset={asset} onClose={handleToggle} />
        </KitAssetsProvider>
      )}
      {variantOpen && variantAsset && (
        <CreateKitVariantModal
          open={variantOpen}
          asset={variantAsset}
          defaultValues={defaultValues}
          onClose={() => setVariantOpen(false)}
        />
      )}
    </Box>
  );
}
